import { useField, useFormikContext } from 'formik';
import styled, { css } from 'styled-components';
import SmartLabel from '../labels/SmartLabel';
import { HTMLInputAutoCompleteAttribute, HTMLInputTypeAttribute, useEffect, useId, useState } from 'react';
import Tooltip, { TooltipPosition } from 'components/controls/Tooltip';
import { toaster } from 'components/v2/toast';
import { NormalizeSpaces } from 'util/utils';

type Props = {
  name: string;
  label?: string;
  tooltip?: string;
  labelStyle?: any;
  tooltipPosition?: TooltipPosition;
  required?: boolean;
  /**
   * static - (default) when the error message appears, it shifts the content below;
   * absolute - prevents layout shifts when error appears and disappears;
   * none - error message doesn't display below the input at all.
   */
  errorPositioning?: 'static' | 'absolute' | 'none';
  error?: string;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  type?: HTMLInputTypeAttribute;
  autoComplete?: HTMLInputAutoCompleteAttribute;
  style?: React.CSSProperties;
  className?: string;
  /** Decides whether the error message renders below the field; defaults to showing any error. */
  shouldShowBottomError?: (error: string) => boolean;
  /** Decides whether the field border is highlighted red; defaults to highlighting on any error. */
  shouldHighlightBorder?: (error: string) => boolean;
  renderErrorMessage?: (error: string) => React.ReactNode;
  /** Plain-text version of the error, shown in a Tooltip when the message is ellipsis-truncated */
  getErrorTooltip?: (error: string) => string;
  /** Strips leading/trailing/double spaces on blur and submit, warning the user via a toast. */
  trim?: boolean;
};

const InputFormField: React.FC<Props> = ({
  name,
  label,
  tooltip,
  labelStyle,
  tooltipPosition,
  errorPositioning = 'static',
  error,
  style,
  className,
  shouldShowBottomError,
  shouldHighlightBorder,
  renderErrorMessage,
  getErrorTooltip,
  trim,
  ...inputProps
}) => {
  const id = useId();
  const [field, meta, helpers] = useField(name);
  const { submitCount } = useFormikContext();
  const rawError = meta.touched ? meta.error || error : undefined;
  const highlightBorder = !!rawError && (shouldHighlightBorder ? shouldHighlightBorder(rawError) : true);
  const showBottomError = !!rawError && (shouldShowBottomError ? shouldShowBottomError(rawError) : true);
  const resolveErrorTooltip = (): string | undefined => {
    if (!rawError) {
      return undefined;
    }
    if (getErrorTooltip) {
      return getErrorTooltip(rawError);
    }

    return renderErrorMessage ? undefined : rawError;
  };
  const errorTooltip = resolveErrorTooltip();

  const applyTrim = (value: string) => {
    const normalized = NormalizeSpaces(value);
    if (normalized !== value) {
      helpers.setValue(normalized);
      toaster({
        severity: 'yellow-exclamation-warning',
        summary: 'Extra spaces have been removed.',
        details: 'Input fields cannot contain leading, trailing or double spaces.',
      });
    }
  };

  useEffect(() => {
    if (trim && !inputProps.disabled && submitCount > 0 && typeof field.value === 'string') {
      applyTrim(field.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (trim && !inputProps.disabled && typeof field.value === 'string') {
      applyTrim(field.value);
    }
    field.onBlur(e);
  };

  return (
    <InputWrapper style={style} className={className}>
      {label && (
        <SmartLabel
          htmlFor={id}
          label={label}
          required={inputProps.required}
          labelStyle={labelStyle}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          hasValue={field.value}
        />
      )}

      {inputProps.type !== 'password' && (
        <Input id={id} $hasError={highlightBorder} {...field} {...inputProps} onBlur={handleBlur} />
      )}
      {inputProps.type === 'password' && (
        <PasswordRevealer
          renderInput={(revealed) => (
            <Input
              id={id}
              $hasError={highlightBorder}
              {...field}
              {...inputProps}
              onBlur={handleBlur}
              type={revealed ? 'text' : 'password'}
            />
          )}
        />
      )}

      {showBottomError && (
        <FieldError $errorPositioning={errorPositioning}>
          {renderErrorMessage ? renderErrorMessage(rawError as string) : rawError}
          {errorTooltip && <Tooltip position="titleEllipsed">{errorTooltip}</Tooltip>}
        </FieldError>
      )}
    </InputWrapper>
  );
};

const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const PasswordRevealer = ({ renderInput }: { renderInput(isRevealed: boolean): React.ReactNode }) => {
  const [isRevealed, setRevealed] = useState<boolean>(false);

  return (
    <PasswordInputWrapper>
      {renderInput(isRevealed)}
      <RevealPasswordButton
        type="button"
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          setRevealed(!isRevealed);
        }}
        aria-label="Reveal"
      >
        <i className={isRevealed ? 'pi pi-eye-slash' : 'pi pi-eye'} />
        <Tooltip position="bottom">{isRevealed ? 'Hide password' : 'Show password'}</Tooltip>
      </RevealPasswordButton>
    </PasswordInputWrapper>
  );
};

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const RevealPasswordButton = styled.button`
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  color: ${(props) => props.theme.colors.primaryBlack};
  padding: 0;
  background: none;
  border: none;
  margin: 0;
  display: block;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
`;

const Input = styled.input<{ $hasError: boolean }>`
  padding: 1rem;
  border: 1px solid
    ${(props) => {
      if (props.disabled) {
        return props.theme.colors.elementsFilledInput;
      }
      if (props.$hasError) {
        return props.theme.colors.systemStatusError;
      }
      return props.theme.colors.elementsInputBorder;
    }};
  border-radius: 2px;
  margin: 0;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.elementsFilledInput : props.theme.colors.primaryWhite};
  color: ${(props) => (props.disabled ? props.theme.colors.primaryBlack70 : props.theme.colors.primaryBlack)};
  outline: none;

  &:focus,
  &:hover {
    border-color: ${(props) => (props.disabled ? 'transparent' : props.theme.colors.primaryOrange)};
  }

  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  height: 44px;

  &[type='password'] {
    /* Makes bullets bigger and more spread */
    font-family: Verdana;
    letter-spacing: 0.125rem;
    &::placeholder {
      font-family: Inter;
      letter-spacing: normal;
    }
  }

  /* Override background & text color UA styles for pre-filled input */
  &:autofill {
    transition: background-color ease-in-out 9999999s !important;
    -webkit-text-fill-color: ${(props) => props.theme.colors.primaryBlack} !important; /* Ensure text color stays black */
  }
`;

const FieldError = styled.div<{ $errorPositioning: 'absolute' | 'static' | 'none' }>`
  margin-top: 4px;
  margin-bottom: 0.5rem;
  font-family: 'Inter';
  font-size: 12px;
  color: ${(props) => props.theme.colors.systemStatusError};

  ${(props) =>
    props.$errorPositioning === 'absolute' &&
    css`
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin: 4px 0 0;
      /* Fits within the 30px grid row-gap so it never overlaps the next row of fields. */
      max-height: 24px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `}

  ${(props) =>
    props.$errorPositioning === 'none' &&
    css`
      display: none;
    `}
`;

export default InputFormField;
